import type { GeoCoordinates } from '@/features/geolocation/types/location';

const LATITUDE_DECIMALS = 7;
const LONGITUDE_DECIMALS = 7;
const ALTITUDE_DECIMALS = 2;
const ALTITUDE_LIMIT = 9999;

function clamp(value: number, limit: number): number {
  return Math.min(Math.max(value, -limit), limit);
}

/**
 * Fixed-point decimal string with a dot separator, ALWAYS locale-independent.
 * Never use `formatNumber()` for coordinates — under `cs` it emits a comma,
 * which the backend regexes reject.
 */
export function toDecimalString(value: number, decimals: number): string {
  return value.toFixed(decimals);
}

/** Leaflet returns un-wrapped lng after world-copy panning (e.g. 374.4378). */
export function wrapLongitude(value: number): number {
  return ((((value + 180) % 360) + 360) % 360) - 180;
}

/** ≤ 3 integer digits + 7 decimals — satisfies `^-?\d{0,3}(?:\.\d{0,7})?$`. */
export const formatLatitude = (value: number): string =>
  toDecimalString(clamp(value, 90), LATITUDE_DECIMALS);

/** ≤ 4 integer digits + 7 decimals — satisfies `^-?\d{0,4}(?:\.\d{0,7})?$`. */
export const formatLongitude = (value: number): string =>
  toDecimalString(wrapLongitude(value), LONGITUDE_DECIMALS);

/** ≤ 4 integer digits + 2 decimals — satisfies `^-?\d{0,4}(?:\.\d{0,2})?$`. */
export const formatAltitude = (value: number): string =>
  toDecimalString(clamp(value, ALTITUDE_LIMIT), ALTITUDE_DECIMALS);

/** Parses an API decimal string. Returns null for anything unusable. */
export function parseCoordinateString(value: string | null | undefined): number | null {
  if (value === null || value === undefined) return null;

  const trimmed = value.trim();
  if (trimmed === '') return null;

  const parsed = Number(trimmed);
  return Number.isFinite(parsed) ? parsed : null;
}

/** Rounded, deterministic cache key — guarantees hits for sub-metre jitter. */
export function coordinateCacheKey(coordinates: GeoCoordinates, decimals: number): string {
  const latitude = toDecimalString(clamp(coordinates.latitude, 90), decimals);
  const longitude = toDecimalString(wrapLongitude(coordinates.longitude), decimals);
  return `${latitude},${longitude}`;
}
